import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { Input } from '@ami/react/core';
import {
  SimpleModal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from '@ami/react/core';
import { makeValidable } from '@ami/react/core';
import { MachineMakeDropdown } from '../../../machines/MachineMakeDropdown';
import { MachineModelDropdown } from '../../../machines/MachineModelDropdown';
import { machineStatus } from '../../../machines/machineStatusType';

const otherMachineId = 'OTHER-MACHINE';

const DataItem = ({ label, children }) => (
  <div className="row">
    <label className="col-sm-3 col-form-label">{label}</label>
    <div className="col-sm-9">
      <div>{children}</div>
    </div>
  </div>
);

const ModalContent = ({
  preferredMakeIds,
  weightCategoryId,
  make,
  model,
  machineId,
  onStateChanged,
  onMachineChanged,
  isOtherMachineBeingEntered,
  onEnterOtherMachineShortcut,
}) => (
  <Fragment>
    <DataItem label="Make" placeholder="Please specify a make">
      <MachineMakeDropdown
        placeholder="Select a make..."
        selectedId={make}
        onChange={(make) =>
          onStateChanged({ make, model: '', machineId: null })
        }
        weightCategoryId={weightCategoryId}
        required
      />
    </DataItem>
    <DataItem label="Model">
      <MachineModelDropdown
        className="mt-2 mb-2"
        placeholder="Select a model..."
        selectedId={isOtherMachineBeingEntered ? otherMachineId : machineId}
        onChangeItem={onMachineChanged}
        make={make}
        weightCategoryId={weightCategoryId}
        includeOtherMachine={true}
        status={machineStatus.Approved}
        preferredMakeIds={preferredMakeIds}
        required
      />
      <button
        type="button"
        className="btn btn-link pull-right ps-2 pt-2 pe-0 pb-2"
        onClick={() => onEnterOtherMachineShortcut()}
      >
        enter other model
      </button>
    </DataItem>
    {isOtherMachineBeingEntered && (
      <DataItem label="Other Model">
        <Input
          className="mb-2"
          placeholder="Enter the machine's model number"
          value={model}
          onChange={(e) =>
            onStateChanged({ make, machineId: null, model: e.target.value })
          }
          required
        />
      </DataItem>
    )}
  </Fragment>
);

const ValidableModalContent = makeValidable(ModalContent);

export default class ReviewQuoteMachineModal extends React.Component {
  constructor(props) {
    super(props);
    this.validableRef = React.createRef();
    this.handleMachineSelected = this.handleMachineSelected.bind(this);
    this.onEnterOtherMachineShortcut =
      this.onEnterOtherMachineShortcut.bind(this);
    this.state = {
      make: null,
      machineId: null,
      model: '',
      isOtherMachineBeingEntered: false,
      showValidations: false,
    };
  }

  static propTypes = {
    manufacturer: PropTypes.string,
    machineId: PropTypes.string,
    weightCategoryId: PropTypes.string.isRequired,
    show: PropTypes.bool.isRequired,
    onSave: PropTypes.func.isRequired,
    onHide: PropTypes.func.isRequired,
    preferredMakeIds: PropTypes.arrayOf(PropTypes.string).isRequired,
    loadingNewConfiguration: PropTypes.bool.isRequired,
  };

  componentDidUpdate(prevProps) {
    if (!prevProps.show && this.props.show) {
      this.setState({
        make: this.props.manufacturer,
        machineId: this.props.machineId,
        model: '',
        isOtherMachineBeingEntered: false,
      });
    }
  }

  save = (e) => {
    if (e) e.preventDefault();
    this.setState({ showValidations: true }, async () => {
      if (this.validableRef.current.isValid()) {
        await this.props.onSave(
          this.state.make,
          this.state.machineId,
          this.state.model
        );
      }
    });
  };

  clearMachineState = () => {
    this.setState({ machineId: null, model: '' });
  };

  onEnterOtherMachineShortcut = () => {
    this.setState({ isOtherMachineBeingEntered: true });
    this.clearMachineState();
  };

  handleMachineSelected = (machine) => {
    const isOtherMachineBeingEntered = machine.id === otherMachineId;
    this.setState({ isOtherMachineBeingEntered });
    if (isOtherMachineBeingEntered) {
      this.clearMachineState();
    } else {
      this.setState({ machineId: machine.id, model: machine.label });
    }
  };

  render() {
    const {
      show,
      onHide,
      loadingNewConfiguration,
      weightCategoryId,
      preferredMakeIds,
    } = this.props;
    const {
      make,
      model,
      machineId,
      isOtherMachineBeingEntered,
      showValidations,
    } = this.state;
    return (
      <SimpleModal show={show} size="md">
        <ModalHeader title="Machine Selection" />
        <ModalBody>
          <ValidableModalContent
            show={show}
            ref={this.validableRef}
            showValidations={showValidations}
            make={make}
            model={model}
            machineId={machineId}
            onStateChanged={(updated) => this.setState(updated)}
            onMachineChanged={this.handleMachineSelected}
            isOtherMachineBeingEntered={isOtherMachineBeingEntered}
            onEnterOtherMachineShortcut={this.onEnterOtherMachineShortcut}
            weightCategoryId={weightCategoryId}
            preferredMakeIds={preferredMakeIds}
          />
        </ModalBody>
        <ModalFooter>
          <button className="btn btn-secondary" onClick={onHide}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={this.save}
            disabled={loadingNewConfiguration}
          >
            {loadingNewConfiguration ? (
              <span>
                <i className="fa fa-spin fa-spinner" /> Applying
              </span>
            ) : (
              <span>Apply</span>
            )}
          </button>
        </ModalFooter>
      </SimpleModal>
    );
  }
}
